import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl, FramerTooltip } from "@ryde/controls";
import { useRideReBuild } from "@ryde/redux";
import {
  CourseRouteTypeEnum,
  errors,
  IRideDto,
  Log,
  rebuildRouteLoadingAtom,
  simpleResultWarnInfo,
  useIsMobile,
  warnAndToastException,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { ButtonHeader } from "../../../../right";

interface IRideRebuildButtonProps {
  rideDto: Partial<IRideDto>;
}

export const RideRebuildButton: FC<IRideRebuildButtonProps> = ({ rideDto }) => {
  const { t } = useTranslation();

  const { isMobile } = useIsMobile();
  const [isLoading, setIsLoading] = useRecoilState(rebuildRouteLoadingAtom);

  const { rideRebuild } = useRideReBuild();

  const handleRebuild = useEvent(async () => {
    if (!rideDto?.rideStart) {
      Log.warnAndToast(t(errors.ride.noInitialDateInRide));
      return;
    }

    // Prevent multiple clicks
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const rideRebuildResult = await rideRebuild(rideDto);

      if (!rideRebuildResult.success) {
        return warnAndToastSimpleResult(rideRebuildResult, t);
      }

      // A rebuild can leave part of the ride alone on purpose — a past ride keeps its station area and price
      // when re-pricing it cannot preserve the supplier, the vehicle type or a matching rate. The API names
      // which of those it was, and that notice replaces the plain success message.
      const skippedPartMessage = simpleResultWarnInfo(rideRebuildResult, t);

      if (skippedPartMessage) {
        Log.warnAndToast(skippedPartMessage);
        return;
      }

      Log.infoAndToast(t("ride.RIDE_REBUILD_SUCCESSFULLY"));
    } catch (error) {
      warnAndToastException({
        error,
        translationFunction: t,
        fallbackTranslationKey: errors.ride.rideRebuildFailed,
      });
    } finally {
      setIsLoading(false);
    }
  });

  const handleButtonClick = useEvent(() => {
    handleRebuild();
  });

  return (
    <AccessControl entity="ride" action="update">
      {rideDto?.courseRouteType !== CourseRouteTypeEnum.Shuttle && (
        <div className="rebuild-route-button-container2">
          <FramerTooltip content={t(`general.REBUILD_RIDE`)}>
            <ButtonHeader onClick={handleButtonClick}>
              <SVG_MUI.RotateRight2SVG data-e2e="rebuild-ride-svg" />
              <span>
                {t(isMobile ? `general.REBUILD` : `general.REBUILD_RIDE`)}
              </span>
            </ButtonHeader>
          </FramerTooltip>
        </div>
      )}
    </AccessControl>
  );
};
